import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import HeroSection from './HeroSection';
import HomeSearchForm from './HomeSearchForm.lazy';
import HomeHeroSlider, { HERO_SLIDES } from './HomeHeroSlider';
import OfferHeroCard from '../../../components/offers/OfferHeroCard';
import FestiveHeroLayer from '../../../views/home/festive/FestiveHeroLayer';
import { isNavratriDasaraSeason } from '../../../views/home/festive/navratriSeason';

const Header = ({ isLoggedIn, loggedCustomer, cities, getProperty, websiteAppearance }) => {
    const [isHome, setIsHome] = useState(false);
    const [bgColor, setBgColor] = useState("transparent");
    const location = useLocation();
    
    useEffect(() => {
        setIsHome(location.pathname === '/' || location.pathname === '');
        // Only jump to top on real page changes — not when query params update
        // (e.g. property detail ?checkIn= / ?checkOut= while picking dates).
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                setBgColor(window.scrollY > 50 ? "#0D1B2A" : "transparent");
                ticking = false;
            });
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Top strip: a coupon on "Top offer strip" wins; otherwise the plain announcement.
    // Nothing shows until the appearance loads — no made-up fallback code.
    const promoBanner = (() => {
        const offer = websiteAppearance?.offerStrip;
        if (offer) {
            return {
                visible: true,
                text: offer.theme?.stripText || offer.title || offer.discountLabel || '',
                code: offer.code,
                link: `/offers/${encodeURIComponent(offer.slug || offer.code)}`,
                offerCode: offer.code,
                style: offer.theme?.style,
            };
        }
        const promo = websiteAppearance?.promo;
        if (!promo) return { visible: false, text: '', code: '', link: null };
        return {
            visible: Boolean(promo.enabled && (String(promo.text || '').trim() || String(promo.code || '').trim())),
            text: promo.text || '',
            code: promo.code || '',
            link: promo.link || null,
        };
    })();

    // Brand slides (CMS or bundled) with coupon offer slides placed before/after them.
    const heroSlides = useMemo(() => {
        const cms = Array.isArray(websiteAppearance?.slides)
            ? websiteAppearance.slides.map((slide) => slide.imageUrl).filter(Boolean)
            : [];
        const brand = (cms.length ? cms : HERO_SLIDES).map((src) => ({ src, offer: null }));
        const offers = (websiteAppearance?.offerSlides || [])
            .filter((o) => o.theme?.heroImageUrl)
            .map((o) => ({ src: o.theme.heroImageUrl, offer: o }));
        if (!offers.length) return null;
        const first = offers.filter((s) => s.offer.theme?.heroPosition !== 'last');
        const last = offers.filter((s) => s.offer.theme?.heroPosition === 'last');
        return [...first, ...brand, ...last];
    }, [websiteAppearance]);
    const heroSlideUrls = heroSlides
        ? heroSlides.map((s) => s.src)
        : Array.isArray(websiteAppearance?.slides) && websiteAppearance.slides.length
            ? websiteAppearance.slides.map((slide) => slide.imageUrl).filter(Boolean)
            : null;
    const [activeSlide, setActiveSlide] = useState(0);
    const onActiveSlide = useCallback((i) => setActiveSlide(i), []);
    const activeOffer = heroSlides?.[activeSlide]?.offer || null;

    const festiveHome = isHome && isNavratriDasaraSeason();

    return (
        <>
            <Box
                className={isHome ? `home-hero-shell${festiveHome ? " home-hero-shell--festive" : ""}` : undefined}
                sx={{ position: "relative", width: "100%" }}
            >
                {isHome && <HomeHeroSlider slideUrls={heroSlideUrls} onActiveChange={onActiveSlide} />}

                {isHome && (
                    <Box
                        className="home-hero-overlay"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0, 0, 0, 0.5)",
                            pointerEvents: "none",
                        }}
                    />
                )}

                {festiveHome && <FestiveHeroLayer />}

                <Navbar
                    isLoggedIn={isLoggedIn}
                    loggedCustomer={loggedCustomer}
                    appBarBackground={isHome ? bgColor : "#0D1B2A"}
                    promoBanner={promoBanner}
                />

                {isHome && <HeroSection />}

                {isHome && activeOffer && <OfferHeroCard key={activeOffer.id} offer={activeOffer} />}

                {isHome && (
                    <Box
                        sx={{
                            position: "absolute",
                            right: { xs: "1rem", md: "2.5rem" },
                            bottom: { xs: "8.5rem", md: "7rem" },
                            display: { xs: "none", md: "flex" },
                            flexDirection: "column",
                            background: "rgba(8,16,32,0.72)",
                            backdropFilter: "blur(20px) saturate(1.5)",
                            border: "1px solid rgba(196,154,60,0.22)",
                            borderRadius: "14px",
                            overflow: "hidden",
                            minWidth: "158px",
                            zIndex: 2
                        }}
                    >
                        <Box sx={{ px: 3, py: 2.2, textAlign: "center" }}>
                            <Box sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: "2rem", fontWeight: 600, color: "#FFE4A6", lineHeight: 1 }}>
                                4.6 <Box component="span" sx={{ color: "#FFBD59", fontSize: "1rem" }}>★</Box>
                            </Box>
                            <Box sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.42)", letterSpacing: "0.09em", textTransform: "uppercase", mt: 0.4 }}>
                                Avg. Guest Rating
                            </Box>
                        </Box>
                        <Box sx={{ borderTop: "1px solid rgba(196,154,60,0.12)", px: 3, py: 2.2, textAlign: "center" }}>
                            <Box sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: "2rem", fontWeight: 600, color: "#FFE4A6", lineHeight: 1 }}>
                                50K+
                            </Box>
                            <Box sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.42)", letterSpacing: "0.09em", textTransform: "uppercase", mt: 0.4 }}>
                                Guests Hosted
                            </Box>
                        </Box>
                        <Box sx={{ borderTop: "1px solid rgba(196,154,60,0.12)", px: 3, py: 2.2, textAlign: "center" }}>
                            <Box sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: "2rem", fontWeight: 600, color: "#FFE4A6", lineHeight: 1 }}>
                                2019
                            </Box>
                            <Box sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.42)", letterSpacing: "0.09em", textTransform: "uppercase", mt: 0.4 }}>
                                Since
                            </Box>
                        </Box>
                    </Box>
                )}

                {isHome && (
                    <HomeSearchForm
                        cities={cities}
                        getProperty={getProperty}
                    />
                )}
            </Box>
        </>
    );
};

export default Header;
